import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Baby, ArrowRight } from "lucide-react";
import { isLowPowerClient } from "../../lib/platform";

const IMG_HI = "https://images.unsplash.com/photo-1532706302136-347336b002ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const IMG_LO = "https://images.unsplash.com/photo-1532706302136-347336b002ec?crop=entropy&cs=srgb&fm=jpg&q=70&w=800";

// Desktop-only parallax wrappers. Mounted only when !lite so mobile creates NO
// scroll subscription (avoids getBoundingClientRect-on-scroll jank on phones).
function ImageParallax({ targetRef }) {
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <motion.div style={{ y }} className="pointer-events-none absolute inset-0 scale-110">
      <img src={IMG_HI} alt="An expecting couple, hands resting together on the bump" className="h-full w-full object-cover" loading="lazy" />
    </motion.div>
  );
}
function CopyParallax({ targetRef, children }) {
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  return <motion.div style={{ y }} className="relative mx-auto w-full max-w-6xl px-6 lg:px-8">{children}</motion.div>;
}

export function PregnancyBand({ onJoin }) {
  const ref = useRef(null);
  const [lite] = useState(() => isLowPowerClient());

  const copy = (
    <div className="max-w-xl">
      <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider" style={{ borderColor: "#E7A6C455", color: "#E7A6C4", background: "#E7A6C41A" }}>
        <Baby size={13} /> Pregnancy &amp; Postpartum · New
      </span>
      <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-mist sm:text-6xl">
        From two lines to the
        <br />
        <span style={{ color: "#E7A6C4" }}>fourth trimester</span> — together.
      </h2>
      <p className="mt-7 max-w-lg text-base leading-relaxed text-mist-muted sm:text-lg">
        The moment you're expecting, WYVN transforms. A warm, week-by-week journey begins — guided by <span className="text-mist">Lumi</span>, our dedicated care coach — with safe movement, daily support, and caring cues so your partner knows exactly how to show up. Right through to postpartum.
      </p>
      <button onClick={onJoin} data-testid="pregnancy-band-cta" className="btn-primary mt-8 inline-flex items-center gap-2">
        Start the journey free <ArrowRight size={16} />
      </button>
    </div>
  );

  return (
    <section ref={ref} id="pregnancy" className="relative flex min-h-[92vh] items-center overflow-hidden bg-ink" data-testid="pregnancy-band">
      {/* full-bleed image — parallax on desktop, static on mobile for smoothness */}
      {lite ? (
        <div className="pointer-events-none absolute inset-0">
          <img src={IMG_LO} alt="An expecting couple, hands resting together on the bump" className="h-full w-full object-cover" loading="lazy" />
        </div>
      ) : (
        <ImageParallax targetRef={ref} />
      )}
      {/* scrims: darken left for legible type, let the photo breathe on the right */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/20" />
      <div className="pointer-events-none absolute -bottom-24 right-0 h-[420px] w-[520px] rounded-full blur-[150px]" style={{ background: "#E7A6C426" }} />

      {lite ? (
        <div className="relative mx-auto w-full max-w-6xl px-6 lg:px-8">{copy}</div>
      ) : (
        <CopyParallax targetRef={ref}>{copy}</CopyParallax>
      )}
    </section>
  );
}
